import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { Mail, Trash2 } from "lucide-react"
import { Avatar } from "@ui/Avatar"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Dialog } from "@ui/Dialog"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { Pill } from "@ui/Pill"
import { Select } from "@ui/Select"
import type { Invitation, OrganizationMember } from "@lib/inertia-types"

interface Org {
  id: number
  name: string
  slug: string
  hosted_domain: string | null
}

export default function OrganizationSettings({
  organization,
  can_manage,
  current_user_id,
  members,
  invitations,
}: {
  organization: Org
  can_manage: boolean
  current_user_id: number
  members: OrganizationMember[]
  invitations: Invitation[]
}) {
  const [inviteOpen, setInviteOpen] = React.useState(false)

  return (
    <>
      <Head title={`${organization.name} · Settings · Fetch`} />
      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-6">
          <h1 className="text-title3 font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
            {organization.name}
          </h1>
          {organization.hosted_domain && (
            <p className="mt-1 text-small text-cloverhound-gray-600">
              Anyone with an <code>@{organization.hosted_domain}</code> Google account joins automatically.
            </p>
          )}
        </div>

        <Card padding="lg" className="mb-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
              Members
            </h2>
            {can_manage && (
              <Button size="sm" onClick={() => setInviteOpen(true)}>
                <Mail className="size-3.5" aria-hidden="true" />
                Invite
              </Button>
            )}
          </div>
          <div className="flex flex-col gap-1">
            {members.map((member) => (
              <div key={member.id} className="flex items-center gap-3 py-1.5">
                <Avatar size="md" alt={member.user.name ?? member.user.email_address} src={member.user.avatar_url ?? undefined} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-small font-medium text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
                    {member.user.name ?? member.user.email_address}
                    {member.user.id === current_user_id && <span className="ml-1 text-mini text-cloverhound-gray-500">(you)</span>}
                  </p>
                  <p className="truncate text-mini text-cloverhound-gray-600">{member.user.email_address}</p>
                </div>
                {can_manage && member.user.id !== current_user_id ? (
                  <>
                    <div className="w-28">
                      <Select.Root
                        value={member.role}
                        onValueChange={(v) => v && router.patch(`/organization/members/${member.id}`, { role: v }, { preserveScroll: true, only: ["members"] })}
                      >
                        <Select.Trigger aria-label="Role" className="h-8">
                          <Select.Value />
                        </Select.Trigger>
                        <Select.Content>
                          <Select.Item value="owner">Owner</Select.Item>
                          <Select.Item value="admin">Admin</Select.Item>
                          <Select.Item value="member">Member</Select.Item>
                        </Select.Content>
                      </Select.Root>
                    </div>
                    <Button
                      variant="ghost"
                      size="iconXs"
                      onClick={() => {
                        if (confirm(`Remove ${member.user.email_address}?`))
                          router.delete(`/organization/members/${member.id}`, { preserveScroll: true, only: ["members"] })
                      }}
                      aria-label="Remove member"
                    >
                      <Trash2 className="size-3.5" aria-hidden="true" />
                    </Button>
                  </>
                ) : (
                  <Pill>{member.role}</Pill>
                )}
              </div>
            ))}
          </div>
        </Card>

        {can_manage && invitations.length > 0 && (
          <Card padding="lg">
            <h2 className="mb-3 text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
              Pending invitations
            </h2>
            <div className="flex flex-col gap-1">
              {invitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center gap-3 py-1.5">
                  <Mail className="size-4 text-cloverhound-gray-400" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-small text-cloverhound-gray-900 dark:text-cloverhound-gray-50">{invitation.email}</p>
                    <p className="text-mini text-cloverhound-gray-500">
                      {invitation.role} · expires {new Date(invitation.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => router.delete(`/invitations/${invitation.id}`, { preserveScroll: true, only: ["invitations"] })}
                    className="text-cloverhound-red-500"
                  >
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </>
  )
}

function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = React.useState("")
  const [role, setRole] = React.useState("member")

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    router.post("/invitations", { email, role }, { preserveScroll: true, only: ["invitations"], onFinish: () => (setEmail(""), onClose()) })
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content size="sm">
        <form onSubmit={submit}>
          <Dialog.Header>
            <Dialog.Title>Invite a teammate</Dialog.Title>
            <Dialog.Description>
              They'll join after signing in with the Google account for this email. A copyable link is created; email is sent if configured.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-4">
            <Field label="Email" required>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@client.com" autoFocus />
            </Field>
            <Field label="Role">
              <Select.Root value={role} onValueChange={(v) => v && setRole(v)}>
                <Select.Trigger aria-label="Role">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="member">Member</Select.Item>
                  <Select.Item value="admin">Admin</Select.Item>
                </Select.Content>
              </Select.Root>
            </Field>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!email.trim()}>
              Create invitation
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}
